import { useCallback, useEffect, useState } from "react";
import {
  clearDeferredInstallPrompt,
  clearPwaInstallMarked,
  getDeferredInstallPrompt,
  isStandaloneDisplay,
  markPwaInstalled,
  type BeforeInstallPromptEvent,
} from "./pwaInstallBridge";

export type PwaInstallOutcome = "accepted" | "dismissed" | "unavailable" | "ios";

const isIosDevice = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

/**
 * Install button should show only when the PWA is installable and not already installed:
 * - Chromium: browser fired `beforeinstallprompt`
 * - iOS Safari: not standalone (manual Add to Home Screen)
 * Early prompt capture lives in `pwaInstallBridge` so reloads still work.
 */
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(() =>
    getDeferredInstallPrompt(),
  );
  const [isInstalled, setIsInstalled] = useState(() => isStandaloneDisplay());
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    setIsIos(isIosDevice());
    setDeferredPrompt(getDeferredInstallPrompt());

    if (isStandaloneDisplay()) {
      markPwaInstalled();
      setIsInstalled(true);
    } else {
      clearPwaInstallMarked();
      setIsInstalled(false);
    }

    const onPromptAvailable = () => {
      setDeferredPrompt(getDeferredInstallPrompt());
      setIsInstalled(false);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
    };
    const onDisplayModeChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        markPwaInstalled();
        setIsInstalled(true);
        clearDeferredInstallPrompt();
        setDeferredPrompt(null);
      } else {
        clearPwaInstallMarked();
        setIsInstalled(false);
      }
    };

    const standaloneMq = window.matchMedia("(display-mode: standalone)");
    standaloneMq.addEventListener?.("change", onDisplayModeChange);
    window.addEventListener("hope:pwa-prompt-available", onPromptAvailable);
    window.addEventListener("hope:pwa-installed", onInstalled);

    return () => {
      standaloneMq.removeEventListener?.("change", onDisplayModeChange);
      window.removeEventListener("hope:pwa-prompt-available", onPromptAvailable);
      window.removeEventListener("hope:pwa-installed", onInstalled);
    };
  }, []);

  const canPromptNative = Boolean(deferredPrompt ?? getDeferredInstallPrompt()) && !isInstalled;
  /** True only when we can actually offer install UX. */
  const canInstall = !isInstalled && (canPromptNative || isIos);

  const promptInstall = useCallback(async (): Promise<PwaInstallOutcome> => {
    if (isStandaloneDisplay()) {
      setIsInstalled(true);
      return "unavailable";
    }
    if (isIos) return "ios";

    const promptEvent = deferredPrompt ?? getDeferredInstallPrompt();
    if (!promptEvent) return "unavailable";

    await promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    clearDeferredInstallPrompt();
    setDeferredPrompt(null);
    if (outcome === "accepted") {
      markPwaInstalled();
      setIsInstalled(true);
    }
    return outcome;
  }, [deferredPrompt, isIos]);

  return {
    isInstalled,
    isIos,
    canPromptNative,
    canInstall,
    promptInstall,
  };
}
